import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ApiFetch } from '../utils/api';
import PinKeypad from '../components/PinKeypad';

const MIN_PIN = 4;
const MAX_PIN = 10;

function HasStoredUsername() {
  return Boolean((localStorage.getItem('riddler_username') || '').trim());
}

function DigitsOnly(value, maxLength = MAX_PIN) {
  return (value || '').replace(/\D/g, '').slice(0, maxLength);
}

export default function Auth() {
  const { login, register, error, setError, isDevEnv } = useAuth();
  const navigate = useNavigate();
  const deviceRegistered = HasStoredUsername();
  // Booth/prod: one phone → one codename (Sign-in only after first register).
  // Dev: always allow Register tab for easier multi-account testing.
  const lockToLogin = deviceRegistered && !isDevEnv;
  const [mode, setMode] = useState(() => (deviceRegistered ? 'login' : 'register'));
  const [username, setUsername] = useState(() => localStorage.getItem('riddler_username') || '');
  const [allocatedUsername, setAllocatedUsername] = useState('');
  const [allocating, setAllocating] = useState(false);
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mintedUsername, setMintedUsername] = useState(null);
  const [topExpanded, setTopExpanded] = useState(() => !deviceRegistered);

  const pinReady = pin.length >= MIN_PIN && pin.length <= MAX_PIN;
  const usernameReady = Boolean(username.trim());

  // Prefetch a unique codename into the register form so username+PIN are filled together for PMs.
  useEffect(() => {
    if (lockToLogin || mode !== 'register' || mintedUsername) return undefined;
    let cancelled = false;
    setAllocating(true);
    ApiFetch('/auth/allocate-username', { method: 'POST', body: '{}' })
      .then((data) => {
        if (!cancelled) setAllocatedUsername(data.username || '');
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setAllocating(false);
      });
    return () => { cancelled = true; };
  }, [lockToLogin, mode, mintedUsername, setError]);

  const switchMode = (next) => {
    if (lockToLogin && next === 'register') return;
    setMode(next);
    setError(null);
    setPin('');
    setMintedUsername(null);
    if (next === 'login') {
      setTopExpanded(false);
    } else {
      setTopExpanded(true);
      setAllocatedUsername('');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!pinReady || submitting || !allocatedUsername || !email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await register(pin, allocatedUsername, email.trim());
      setMintedUsername(data.user.username);
      setPin('');
      setEmail('');
    } catch {
      // error on context
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!usernameReady || !pinReady) {
      setError('Enter your username/email and PIN to sign in');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await login(username.trim(), pin);
      navigate('/chat', { replace: true });
    } catch {
      // error on context
    } finally {
      setSubmitting(false);
    }
  };

  if (mintedUsername) {
    return (
      <div className="auth-screen">
        <div className="disclosure-glow" aria-hidden="true" />
        <main className="auth-panel codename-panel">
          <p className="brand-eyebrow">Your codename</p>
          <h1 className="codename-reveal">{mintedUsername}</h1>
          <p className="auth-sub">
            You&apos;re in. Remember this name with your PIN for sign-in later.
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/chat', { replace: true })}
          >
            Enter the puzzle box
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="disclosure-glow" aria-hidden="true" />
      <main className="auth-panel">
        <button type="button" className="back-link" onClick={() => navigate('/')}>
          ← Back
        </button>

        {!topExpanded ? (
          <button
            type="button"
            className="auth-top-toggle"
            aria-expanded="false"
            onClick={() => setTopExpanded(true)}
          >
            <span>Sign-in options</span>
            <span className="auth-top-toggle-hint">Show</span>
          </button>
        ) : (
          <div className="auth-top">
            <p className="auth-sub">
              {lockToLogin
                ? 'Welcome back. Enter your PIN to continue. This phone already has a codename.'
                : mode === 'register'
                  ? 'Pick a PIN (4–10 digits). Your Batman-themed codename is ready below.'
                  : 'Enter your codename or email to continue.'}
            </p>

            {!lockToLogin && (
              <div className="auth-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  className={mode === 'register' ? 'active' : ''}
                  aria-selected={mode === 'register'}
                  onClick={() => switchMode('register')}
                >
                  Register
                </button>
                <button
                  type="button"
                  role="tab"
                  className={mode === 'login' ? 'active' : ''}
                  aria-selected={mode === 'login'}
                  onClick={() => switchMode('login')}
                >
                  Sign in
                </button>
              </div>
            )}
          </div>
        )}

        {mode === 'register' && (
          <form
            id="riddler-register"
            className="auth-form"
            method="post"
            autoComplete="on"
            onSubmit={handleRegister}
          >
            {/* Visually hidden but present for password managers (value set before PIN submit). */}
            <label htmlFor="register-username" className="sr-only">Codename</label>
            <input
              id="register-username"
              name="username"
              type="text"
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="pm-username-slot"
              value={allocatedUsername}
              onChange={() => {}}
              readOnly
              required
            />
            <label htmlFor="register-email">Email</label>
            <input
              id="register-email"
              name="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="auth-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              disabled={submitting}
            />
            <label htmlFor="register-password">PIN</label>
            <input
              id="register-password"
              name="password"
              className="pin-field"
              type="text"
              autoComplete="new-password"
              inputMode="none"
              pattern="[0-9]*"
              minLength={MIN_PIN}
              maxLength={MAX_PIN}
              passwordrules={`minlength: ${MIN_PIN}; maxlength: ${MAX_PIN}; allowed: digit;`}
              value={pin}
              onChange={(e) => setPin(DigitsOnly(e.target.value))}
              required
              placeholder="••••"
              disabled={allocating || !allocatedUsername}
            />
            <p className="auth-privacy pin-count-line" aria-live="polite">
              {pin.length > 0 && pin.length < MIN_PIN
                ? `Enter at least ${MIN_PIN} digits (${MIN_PIN - pin.length} more).`
                : `${pin.length}/${MAX_PIN} digits · Suggest Phone Number (memorable)`}
            </p>
            <PinKeypad
              value={pin}
              onChange={setPin}
              maxLength={MAX_PIN}
              disabled={submitting || allocating || !allocatedUsername}
            />
            {error && <p className="form-error" role="alert">{error}</p>}
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !pinReady || allocating || !allocatedUsername}
            >
              {submitting ? 'One moment…' : 'Register & play'}
            </button>
          </form>
        )}

        {mode === 'login' && (
          <form
            id="riddler-login"
            className="auth-form"
            method="post"
            autoComplete="on"
            onSubmit={handleLogin}
          >
            <label htmlFor="login-username">Username or email</label>
            <input
              id="login-username"
              name="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="you@example.com"
            />

            <div className="auth-pin-block">
              <label htmlFor="login-password">PIN</label>
              <input
                id="login-password"
                name="password"
                className="pin-field"
                type="text"
                autoComplete="current-password"
                inputMode="none"
                pattern="[0-9]*"
                maxLength={MAX_PIN}
                value={pin}
                onChange={(e) => setPin(DigitsOnly(e.target.value))}
                required
                placeholder="••••"
                enterKeyHint="done"
              />
              <>
                <p className="auth-privacy pin-count-line" aria-live="polite">
                  {pin.length > 0 && pin.length < MIN_PIN
                    ? `Enter at least ${MIN_PIN} digits (${MIN_PIN - pin.length} more).`
                    : `${pin.length}/${MAX_PIN} digits · Suggest Phone Number (memorable)`}
                </p>
                <PinKeypad
                  value={pin}
                  onChange={setPin}
                  maxLength={MAX_PIN}
                  disabled={submitting}
                />
              </>
            </div>

            {error && <p className="form-error" role="alert">{error}</p>}

            <button type="submit" className="btn-primary" disabled={submitting || !usernameReady || !pinReady}>
              {submitting ? 'One moment…' : 'Sign in'}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
